import React, { useState, useEffect } from 'react';
import { api } from '@/contexts/AuthContext';
import { useAuth } from '@/contexts/AuthContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Phone, MapPin, Building2, Save, UserPlus, MessageSquare, ExternalLink, User, CalendarPlus, Send, PhoneCall, Mail, MapPinned, Clock, ArrowRight, CheckCircle, CalendarDays, FileText, BellRing, RefreshCw, Trash2, Store, Sparkles, Target } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import CallScriptDialog from '@/components/CallScriptDialog';
import VoiceNoteButton from '@/components/VoiceNoteButton';
import AISummaryBanner from '@/components/AISummaryBanner';
import PostCallEmailDialog from '@/components/PostCallEmailDialog';
import ProspectionEmailDialog from '@/components/ProspectionEmailDialog';

const COMMERCIAL_OPTIONS = [
  { value: 'nouveau', label: 'Nouveau' },
  { value: 'a_appeler', label: 'À appeler' },
  { value: 'appele', label: 'Appelé' },
  { value: 'rdv_obtenu', label: 'RDV obtenu' },
  { value: 'en_negociation', label: 'En négociation' },
  { value: 'refus', label: 'Refus' },
  { value: 'rappel_demande', label: 'Rappel demandé' },
  { value: 'injoignable', label: 'Injoignable' },
  { value: 'mauvais_numero', label: 'Mauvais numéro' },
  { value: 'converti', label: 'Converti' },
];

import { PRESTATAIRES } from '@/constants/prestataires';

const ACTIVITY_ICONS = {
  commentaire: MessageSquare,
  appel: PhoneCall,
  statut: ArrowRight,
  note: MessageSquare,
  telephone: Phone,
  assignation: UserPlus,
  relance: CalendarPlus,
  modification: Save,
  email_rdv: BellRing,
  email_rdv_erreur: BellRing,
};

const ACTIVITY_COLORS = {
  commentaire: '#0055FF',
  appel: '#10B981',
  statut: '#F97316',
  note: '#8B5CF6',
  telephone: '#EC4899',
  assignation: '#14B8A6',
  relance: '#F59E0B',
  modification: '#71717A',
  email_rdv: '#F97316',
  email_rdv_erreur: '#EF4444',
};

function AutoSavePill({ status }) {
  // 'idle' | 'pending' | 'saving' | 'saved'
  if (status === 'idle') return null;
  const cfg = {
    pending: { dot: '#F97316', text: '#9A3412', bg: '#FFF7ED', border: '#F97316', label: 'Modifs en attente…' },
    saving: { dot: '#0055FF', text: '#1E40AF', bg: '#EFF6FF', border: '#0055FF', label: 'Sauvegarde…' },
    saved: { dot: '#10B981', text: '#065F46', bg: '#ECFDF5', border: '#10B981', label: 'Sauvegardé' },
  }[status];
  return (
    <span
      className="text-[10px] font-medium px-2 py-0.5 rounded-full border inline-flex items-center gap-1.5 transition-all"
      style={{ backgroundColor: `${cfg.border}10`, borderColor: `${cfg.border}40`, color: cfg.text }}
      data-testid={`autosave-pill-${status}`}
    >
      <span
        className="w-1.5 h-1.5 rounded-full"
        style={{ backgroundColor: cfg.dot, ...(status === 'saving' ? { animation: 'pulse 1s infinite' } : {}) }}
      />
      {cfg.label}
    </span>
  );
}

export default function ProspectDrawer({ prospect: initialProspect, onClose, onUpdate, commerciaux = [] }) {
  const { user } = useAuth();
  const [prospect, setProspect] = useState(initialProspect);
  const [commercials, setCommercials] = useState([]);  // Carte commerciaux Wasabi
  const [savingCommercial, setSavingCommercial] = useState(false);
  const [editPhone, setEditPhone] = useState('');
  const [comment, setComment] = useState('');
  const [statut, setStatut] = useState('');
  const [assignTo, setAssignTo] = useState('');
  const [prenomDir, setPrenomDir] = useState('');
  const [nomDir, setNomDir] = useState('');
  const [prestataireActuel, setPrestataireActuel] = useState('Aucun');
  const [prestataireAutre, setPrestataireAutre] = useState('');
  const [emailPro, setEmailPro] = useState('');
  const [caAliment, setCaAliment] = useState('');
  // Identification fields (editable with auto-save)
  const [enseigne, setEnseigne] = useState('');
  const [nomMagasin, setNomMagasin] = useState('');
  const [rue, setRue] = useState('');
  const [codePostal, setCodePostal] = useState('');
  const [ville, setVille] = useState('');
  // Campaign reassignment
  const [campagneId, setCampagneId] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [relanceDate, setRelanceDate] = useState(null);
  const [relanceType, setRelanceType] = useState('telephone');
  const [relanceCreneau, setRelanceCreneau] = useState('');
  const [relanceCommentaire, setRelanceCommentaire] = useState('');
  const [activites, setActivites] = useState([]);
  const [relances, setRelances] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enrichUpdated, setEnrichUpdated] = useState(false); // flash success on enrichment complete
  const [autoSaveStatus, setAutoSaveStatus] = useState('idle'); // 'idle' | 'pending' | 'saving' | 'saved'
  // Guards the auto-save from firing BEFORE the API fetch hydrates state from DB.
  // Without this, the initial render (with empty state strings) would diff against
  // the prop's full values and queue a PUT that wipes fields.
  const [isHydrated, setIsHydrated] = useState(false);

  useEffect(() => {
    api.get('/commercials').then(r => setCommercials(r.data || [])).catch(() => {});
    api.get('/campaigns').then(r => setCampaigns(r.data || [])).catch(() => {});
  }, []);

  const assignCommercial = async (commercial_id) => {
    if (!prospect?.id) return;
    setSavingCommercial(true);
    try {
      const { data } = await api.put(`/prospects/${prospect.id}/assign-commercial`, {
        commercial_id: commercial_id === '__none__' ? null : commercial_id,
      });
      setProspect(p => ({ ...p,
        commercial_assigne_id: data.commercial_assigne_id,
        commercial_assigne_nom: data.commercial_assigne_nom,
        commercial_assigne_color: data.commercial_assigne_color,
      }));
      onUpdate && onUpdate();
      toast.success('Commercial mis à jour');
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setSavingCommercial(false);
  };

  useEffect(() => {
    if (initialProspect?.id) {
      // Reset hydration guard whenever a new prospect is loaded into the drawer
      setIsHydrated(false);
      api.get(`/prospects/${initialProspect.id}`).then(r => {
        const d = r.data;
        setProspect(d);
        setEditPhone(d.telephone || '');
        setComment(d.commentaire || '');
        setStatut(d.statut_commercial || 'nouveau');
        setAssignTo(d.assigne_a || '');
        setPrenomDir(d.prenom_dirigeant || '');
        setNomDir(d.nom_dirigeant || '');
        setPrestataireActuel(d.prestataire_actuel || 'Aucun');
        setPrestataireAutre(d.prestataire_autre || '');
        setEmailPro(d.email_professionnel || '');
        setCaAliment(d.ca_alimentaire_estimatif ? String(d.ca_alimentaire_estimatif) : '');
        setEnseigne(d.enseigne || '');
        setNomMagasin(d.nom_magasin || '');
        setRue(d.rue || '');
        setCodePostal(d.code_postal || '');
        setVille(d.ville || '');
        setCampagneId(d.campagne_id || '');
        setActivites(d.activites || []);
        setRelances(d.relances || []);
        // Only enable auto-save AFTER the state is fully hydrated from DB.
        // setState batches; the flag flip will be visible on the next render.
        setIsHydrated(true);
      }).catch(() => {});
    }
  }, [initialProspect?.id]);

  // Poll for enrichment completion (when prospect freshly created with statut_recherche === en_attente)
  useEffect(() => {
    if (!prospect?.id) return;
    const isEnriching = prospect.statut_recherche === 'en_attente' &&
      (!prospect.telephone || prospect.telephone === '?' || !prospect.nom_dirigeant);
    if (!isEnriching) return;
    let attempts = 0;
    const maxAttempts = 24; // 2 minutes @ 5s
    const timer = setInterval(async () => {
      attempts++;
      try {
        const { data } = await api.get(`/prospects/${prospect.id}`);
        if (data.statut_recherche !== 'en_attente' || (data.telephone && data.telephone !== '?') || data.nom_dirigeant) {
          setProspect(data);
          setEditPhone(data.telephone || '');
          setPrenomDir(data.prenom_dirigeant || '');
          setNomDir(data.nom_dirigeant || '');
          setEnrichUpdated(true);
          setTimeout(() => setEnrichUpdated(false), 4000);
          clearInterval(timer);
        }
      } catch {}
      if (attempts >= maxAttempts) clearInterval(timer);
    }, 5000);
    return () => clearInterval(timer);
  }, [prospect?.id, prospect?.statut_recherche, prospect?.telephone, prospect?.nom_dirigeant]);

  // Compute pending updates without sending. Used by saveChanges() and
  // by guards before status-changing actions (RDV, refus, injoignable…).
  const collectPendingUpdates = () => {
    if (!prospect) return {};
    const updates = {};
    if (editPhone !== prospect.telephone) updates.telephone = editPhone;
    if (comment !== prospect.commentaire) updates.commentaire = comment;
    if (assignTo && assignTo !== prospect.assigne_a) updates.assigne_a = assignTo;
    if (prenomDir !== (prospect.prenom_dirigeant || '')) updates.prenom_dirigeant = prenomDir;
    if (nomDir !== (prospect.nom_dirigeant || '')) updates.nom_dirigeant = nomDir;
    if (prestataireActuel !== (prospect.prestataire_actuel || 'Aucun')) updates.prestataire_actuel = prestataireActuel;
    if (prestataireAutre !== (prospect.prestataire_autre || '')) updates.prestataire_autre = prestataireAutre;
    if (emailPro.trim() !== (prospect.email_professionnel || '').trim()) updates.email_professionnel = emailPro.trim();
    const caNum = parseInt(caAliment.replace(/\D/g, ''), 10) || 0;
    if (caNum !== (prospect.ca_alimentaire_estimatif || 0)) updates.ca_alimentaire_estimatif = caNum;
    if (enseigne !== (prospect.enseigne || '')) updates.enseigne = enseigne;
    if (nomMagasin !== (prospect.nom_magasin || '')) updates.nom_magasin = nomMagasin;
    if (rue !== (prospect.rue || '')) updates.rue = rue;
    if (codePostal !== (prospect.code_postal || '')) updates.code_postal = codePostal;
    if (ville !== (prospect.ville || '')) updates.ville = ville;
    if (campagneId !== (prospect.campagne_id || '')) updates.campagne_id = campagneId;
    return updates;
  };

  const hasPendingFieldEdits = () => {
    const p = collectPendingUpdates();
    return Object.keys(p).length > 0;
  };

  // Persist any pending field edits silently. Returns true on success, false on error.
  // Used by the debounced auto-save AND as a safety net before status-change actions.
  const flushPendingEdits = async () => {
    const updates = collectPendingUpdates();
    if (Object.keys(updates).length === 0) return true;
    setAutoSaveStatus('saving');
    try {
      const { data } = await api.put(`/prospects/${prospect.id}`, updates);
      setProspect(data);
      refreshActivities();
      onUpdate?.();
      setAutoSaveStatus('saved');
      // Reset to idle 2.5s later if no new edits arrived
      setTimeout(() => setAutoSaveStatus(s => (s === 'saved' ? 'idle' : s)), 2500);
      return true;
    } catch (e) {
      setAutoSaveStatus('idle');
      toast.error(e?.response?.data?.detail || 'Échec de la sauvegarde des modifications');
      return false;
    }
  };

  // Debounced auto-save: 600ms after the user stops editing any monitored field,
  // pending changes are silently persisted. Like Notion/Linear — no need to click "Enregistrer".
  // CRITICAL: guarded by `isHydrated` to prevent wiping fields on initial mount
  // (state vars start empty, prop has data → diff would PUT empty strings).
  useEffect(() => {
    if (!prospect?.id) return;
    if (!isHydrated) return;
    if (!hasPendingFieldEdits()) return;
    setAutoSaveStatus('pending');
    const timer = setTimeout(() => {
      flushPendingEdits();
    }, 600);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editPhone, prenomDir, nomDir, prestataireActuel, prestataireAutre, emailPro, caAliment, comment, assignTo, enseigne, nomMagasin, rue, codePostal, ville, campagneId, isHydrated]);

  const saveChanges = async () => {
    setLoading(true);
    try {
      const updates = collectPendingUpdates();
      if (statut !== prospect.statut_commercial) updates.statut_commercial = statut;
      if (Object.keys(updates).length > 0) {
        const { data } = await api.put(`/prospects/${prospect.id}`, updates);
        setProspect(data);
        toast.success('Prospect mis à jour');
        refreshActivities();
        onUpdate?.();
      }
    } catch { toast.error('Erreur de mise à jour'); }
    setLoading(false);
  };

  const addCallResult = async (resultat) => {
    // For RDV obtenu: open the date/time dialog first (user flow)
    if (resultat === 'rdv_obtenu') {
      // Persist any pending field edits (téléphone, dirigeant, email pro…)
      // before the RDV dialog opens, so the email uses fresh data.
      const ok = await flushPendingEdits();
      if (!ok) return;
      const now = new Date();
      now.setDate(now.getDate() + 7); // default: 1 week from now
      setRdvDate(now.toISOString().slice(0, 10));
      setRdvHeure('14:00');
      setRdvNote(comment || '');
      setRdvDateDialogOpen(true);
      return;
    }
    // Other call results: also flush pending edits to keep the prospect in sync
    const ok = await flushPendingEdits();
    if (!ok) return;
    try {
      const { data } = await api.post(`/prospects/${prospect.id}/call-result`, { resultat, commentaire: comment });
      setProspect(data);
      setStatut(resultat);
      toast.success('Résultat enregistré');
      refreshActivities();
      onUpdate?.();
    } catch { toast.error('Erreur'); }
  };

  const [rdvDateDialogOpen, setRdvDateDialogOpen] = useState(false);
  const [rdvDate, setRdvDate] = useState('');
  const [rdvHeure, setRdvHeure] = useState('14:00');
  const [rdvNote, setRdvNote] = useState('');
  const [savingRdv, setSavingRdv] = useState(false);

  const saveRdvAndPickRecipients = async () => {
    if (!rdvDate) { toast.error('Sélectionnez une date'); return; }
    setSavingRdv(true);
    try {
      // Belt-and-suspenders: re-flush pending edits in case the user edited
      // a field while the RDV dialog was open (so the auto-email uses fresh data).
      const flushOk = await flushPendingEdits();
      if (!flushOk) { setSavingRdv(false); return; }
      const { data } = await api.post(`/prospects/${prospect.id}/call-result`, {
        resultat: 'rdv_obtenu',
        commentaire: rdvNote || comment,
        rdv_date_obtenu: rdvDate,
        rdv_heure_obtenu: rdvHeure,
        skip_auto_email: true,
      });
      setProspect(data);
      setStatut('rdv_obtenu');
      toast.success('RDV enregistré');
      refreshActivities();
      onUpdate?.();
      setRdvDateDialogOpen(false);
      // Now open the recipients dialog
      await openRdvDialog();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur');
    }
    setSavingRdv(false);
  };

  const postComment = async () => {
    if (!newComment.trim()) return;
    try {
      await api.post(`/prospects/${prospect.id}/activite`, { contenu: newComment, type_activite: 'commentaire' });
      setNewComment('');
      toast.success('Commentaire ajouté');
      refreshActivities();
    } catch { toast.error('Erreur'); }
  };

  const createRelance = async () => {
    if (!relanceDate) { toast.error('Choisissez une date'); return; }
    const dateStr = format(relanceDate, 'yyyy-MM-dd');
    try {
      await api.post('/relances', {
        prospect_id: prospect.id,
        date_relance: dateStr,
        type_relance: relanceType,
        creneau: relanceCreneau,
        commentaire: relanceCommentaire
      });
      toast.success('Relance programmée');
      setRelanceDate(null);
      setRelanceCreneau('');
      setRelanceCommentaire('');
      refreshActivities();
      const { data } = await api.get(`/prospects/${prospect.id}`);
      setRelances(data.relances || []);
      onUpdate?.();
    } catch { toast.error('Erreur'); }
  };

  const markRelanceDone = async (relanceId) => {
    try {
      await api.put(`/relances/${relanceId}`, { statut: 'effectuee' });
      toast.success('Relance effectuée');
      const { data } = await api.get(`/prospects/${prospect.id}`);
      setRelances(data.relances || []);
      refreshActivities();
    } catch { toast.error('Erreur'); }
  };

  const refreshActivities = async () => {
    try {
      const { data } = await api.get(`/prospects/${prospect.id}/activites`);
      setActivites(data);
    } catch {}
  };

  const downloadPdf = async () => {
    try {
      const response = await api.get(`/prospects/${prospect.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      const cd = response.headers['content-disposition'] || '';
      const m = cd.match(/filename="([^"]+)"/);
      link.download = m ? m[1] : `fiche_${prospect.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Fiche PDF téléchargée');
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const sendRdvEmail = async () => {
    try {
      const { data } = await api.post(`/prospects/${prospect.id}/send-rdv-email`, {});
      toast.success(data.message || 'Email RDV envoyé');
      refreshActivities();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi de l'email RDV");
    }
  };

  const [rdvDialogOpen, setRdvDialogOpen] = useState(false);
  const [availableRecipients, setAvailableRecipients] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [sendingRdv, setSendingRdv] = useState(false);

  const openRdvDialog = async () => {
    try {
      const { data } = await api.get('/notifications/recipients');
      setAvailableRecipients(data || []);
      setSelectedEmails((data || []).map(r => r.email));
      setRdvDialogOpen(true);
    } catch {
      // Fallback to direct send
      sendRdvEmail();
    }
  };

  const toggleRecipient = (email) => {
    setSelectedEmails(list => list.includes(email) ? list.filter(e => e !== email) : [...list, email]);
  };

  const confirmSendRdv = async () => {
    if (selectedEmails.length === 0) {
      toast.error('Sélectionnez au moins un destinataire');
      return;
    }
    setSendingRdv(true);
    try {
      const { data } = await api.post(`/prospects/${prospect.id}/send-rdv-email`, { destinataires: selectedEmails });
      toast.success(data.message || 'Email RDV envoyé');
      refreshActivities();
      setRdvDialogOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'envoi");
    }
    setSendingRdv(false);
  };

  const canManage = user?.role === 'super_admin' || user?.role === 'admin';

  const deleteProspect = async () => {
    const storeName = prospect.nom_magasin || prospect.enseigne || 'ce prospect';
    if (!window.confirm(`Supprimer définitivement "${storeName}" ? Toutes ses activités et relances seront également supprimées.`)) return;
    try {
      await api.delete(`/prospects/${prospect.id}`);
      toast.success('Prospect supprimé');
      onUpdate?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de suppression");
    }
  };

  const [reSearching, setReSearching] = useState(false);
  const reSearch = async () => {
    setReSearching(true);
    try {
      toast.info('Recherche en cours (téléphone + dirigeant)...');
      await api.post(`/prospects/${prospect.id}/re-search`);
      toast.success('Recherche terminée');
      if (onUpdate) onUpdate();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur de recherche");
    }
    setReSearching(false);
  };

  if (!prospect) return null;

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    const now = new Date();
    const diffMs = now - date;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "À l'instant";
    if (diffMin < 60) return `Il y a ${diffMin} min`;
    if (diffMin < 1440) return `Il y a ${Math.floor(diffMin / 60)}h`;
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-[520px] sm:max-w-[520px] overflow-y-auto p-0 border-l border-[#E5E7EB]" data-testid="prospect-drawer">
        <SheetHeader className="p-5 pb-3 border-b border-[#E5E7EB] bg-white sticky top-0 z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <SheetTitle className="text-lg font-bold truncate" style={{ fontFamily: 'var(--font-heading)' }}>
                {prospect.nom_magasin || prospect.enseigne}
              </SheetTitle>
              <div className="flex gap-2 mt-1">
                <Badge variant="secondary" className={`text-xs status-${prospect.statut_recherche}`}>{prospect.statut_recherche}</Badge>
                <Badge variant="secondary" className={`text-xs commercial-${prospect.statut_commercial}`}>{prospect.statut_commercial}</Badge>
              </div>
            </div>
            <div className="flex gap-1.5 shrink-0">
              <CallScriptDialog
                prospectId={prospect.id}
                prospectName={prospect.nom_magasin || prospect.enseigne}
                trigger={
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1.5 border-[#F97316] text-[#F97316] hover:bg-[#FFF7ED]"
                    data-testid="open-script-ia-btn"
                    title="Générer un script d'appel personnalisé par IA"
                  >
                    <Sparkles className="w-3.5 h-3.5" /> Script IA
                  </Button>
                }
              />
              <PostCallEmailDialog
                prospectId={prospect.id}
                prospectName={prospect.nom_magasin || prospect.enseigne}
                prospectEmail={prospect.email_professionnel}
                trigger={
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1.5 border-[#0055FF] text-[#0055FF] hover:bg-[#EFF6FF]"
                    data-testid="open-email-ia-btn"
                    title="Générer un email post-appel par IA"
                  >
                    <Mail className="w-3.5 h-3.5" /> Email IA
                  </Button>
                }
              />
              <ProspectionEmailDialog
                prospect={prospect}
                onSent={() => onUpdate?.()}
                trigger={
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1.5 border-[#047857] text-[#047857] hover:bg-[#ECFDF5]"
                    data-testid="open-prospection-email-btn"
                    title="Envoyer un email de prospection"
                  >
                    <Send className="w-3.5 h-3.5" /> Envoyer un email
                  </Button>
                }
              />
              <VoiceNoteButton
                prospectId={prospect.id}
                onTranscribed={() => refreshActivities()}
                className="h-8"
              />
              <Button
                onClick={reSearch}
                disabled={reSearching}
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 border-[#0055FF] text-[#0055FF] hover:bg-[#EFF6FF]"
                data-testid="research-prospect-btn"
                title="Re-rechercher téléphone + dirigeant"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${reSearching ? 'animate-spin' : ''}`} /> Re-rechercher
              </Button>
              <Button
                onClick={downloadPdf}
                size="sm"
                variant="outline"
                className="h-8 gap-1.5 border-[#F97316] text-[#F97316] hover:bg-[#FFF7ED]"
                data-testid="export-pdf-btn"
                title="Exporter la fiche en PDF"
              >
                <FileText className="w-3.5 h-3.5" /> PDF
              </Button>
              {canManage && (
                <Button
                  onClick={deleteProspect}
                  size="sm"
                  variant="outline"
                  className="h-8 w-8 p-0 border-[#EF4444] text-[#EF4444] hover:bg-[#FEF2F2]"
                  data-testid="delete-prospect-btn"
                  title="Supprimer définitivement"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>

        <Tabs defaultValue="fiche" className="w-full">
          <TabsList className="w-full justify-start rounded-none border-b border-[#E5E7EB] bg-white h-10 px-5">
            <TabsTrigger value="fiche" className="text-xs" data-testid="tab-fiche">Fiche</TabsTrigger>
            <TabsTrigger value="timeline" className="text-xs" data-testid="tab-timeline">
              Suivi ({activites.length})
            </TabsTrigger>
            <TabsTrigger value="relances" className="text-xs" data-testid="tab-relances">
              Relances ({relances.filter(r => r.statut === 'planifiee').length})
            </TabsTrigger>
          </TabsList>

          {/* TAB: Fiche */}
          <TabsContent value="fiche" className="p-5 space-y-5 mt-0">
            {/* Enrichment status banner */}
            {prospect.statut_recherche === 'en_attente' && (!prospect.telephone || prospect.telephone === '?' || !prospect.nom_dirigeant) ? (
              <div className="flex items-start gap-2.5 rounded-lg border border-[#0055FF]/30 bg-[#EFF6FF] p-3 animate-pulse-subtle" data-testid="enrich-banner-loading">
                <RefreshCw className="w-4 h-4 text-[#0055FF] animate-spin mt-0.5 flex-shrink-0"/>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#0055FF]">Enrichissement automatique en cours…</div>
                  <div className="text-[11px] text-[#1E40AF] mt-0.5">Recherche du téléphone (SerpAPI) et du dirigeant (Pappers). Mise à jour automatique ci-dessous dans quelques secondes.</div>
                </div>
              </div>
            ) : enrichUpdated ? (
              <div className="flex items-start gap-2.5 rounded-lg border border-[#10B981]/40 bg-[#ECFDF5] p-3" data-testid="enrich-banner-success">
                <CheckCircle className="w-4 h-4 text-[#10B981] mt-0.5 flex-shrink-0"/>
                <div className="flex-1">
                  <div className="text-sm font-semibold text-[#065F46]">Enrichissement terminé ✓</div>
                  <div className="text-[11px] text-[#059669] mt-0.5">
                    {prospect.telephone && prospect.telephone !== '?' && <>📞 {prospect.telephone}</>}
                    {prospect.nom_dirigeant && <> • 👤 {prospect.prenom_dirigeant} {prospect.nom_dirigeant}</>}
                    {prospect.score_confiance > 0 && <> • Score {prospect.score_confiance}%</>}
                  </div>
                </div>
              </div>
            ) : null}

            {/* AI Summary Banner */}
            <AISummaryBanner prospectId={prospect.id} />

            {/* Store Info — editable with auto-save */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-[#71717A] uppercase tracking-wider flex items-center gap-2">
                <Store className="w-3.5 h-3.5" /> Magasin
              </h4>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-[10px] text-[#71717A] uppercase tracking-wider">Enseigne</Label>
                  <Input value={enseigne} onChange={e => setEnseigne(e.target.value)} placeholder="Enseigne"
                    className="h-9 bg-white border-[#E5E7EB] text-sm" data-testid="store-enseigne" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#71717A] uppercase tracking-wider">Nom du magasin</Label>
                  <Input value={nomMagasin} onChange={e => setNomMagasin(e.target.value)} placeholder="Nom magasin"
                    className="h-9 bg-white border-[#E5E7EB] text-sm" data-testid="store-nom-magasin" />
                </div>
                <div className="col-span-2">
                  <Label className="text-[10px] text-[#71717A] uppercase tracking-wider">Adresse</Label>
                  <Input value={rue} onChange={e => setRue(e.target.value)} placeholder="Rue, n°, complément"
                    className="h-9 bg-white border-[#E5E7EB] text-sm" data-testid="store-rue" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#71717A] uppercase tracking-wider">Code postal</Label>
                  <Input value={codePostal} onChange={e => setCodePostal(e.target.value)} placeholder="75001" maxLength={5}
                    className="h-9 bg-white border-[#E5E7EB] text-sm font-mono" data-testid="store-cp" />
                </div>
                <div>
                  <Label className="text-[10px] text-[#71717A] uppercase tracking-wider">Ville</Label>
                  <Input value={ville} onChange={e => setVille(e.target.value)} placeholder="Ville"
                    className="h-9 bg-white border-[#E5E7EB] text-sm" data-testid="store-ville" />
                </div>
                {prospect.marque && (
                  <div className="col-span-2 flex items-center gap-2 text-xs text-[#71717A] pt-1">
                    <Building2 className="w-3.5 h-3.5" /> Marque : <span className="font-medium text-[#18181B]">{prospect.marque}</span>
                  </div>
                )}
              </div>
            </div>

            <Separator />

            {/* Campagne — rattachement modifiable */}
            <div className="space-y-2">
              <h4 className="text-xs font-semibold text-[#71717A] uppercase tracking-wider flex items-center gap-2">
                <Target className="w-3.5 h-3.5" /> Campagne
              </h4>
              <Select value={campagneId || '__none__'} onValueChange={(v) => setCampagneId(v === '__none__' ? '' : v)}>
                <SelectTrigger className="h-9 bg-white border-[#E5E7EB] text-sm" data-testid="prospect-campagne-select">
                  <SelectValue placeholder="Aucune campagne" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Aucune campagne</SelectItem>
                  {campaigns.map(c => (
                    <SelectItem key={c.id} value={c.id}>{c.nom}{c.statut ? ` · ${c.statut}` : ''}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#71717A]">Déplacer cette fiche vers une autre campagne. Sauvegarde automatique.</p>
            </div>

            <Separator />

            {/* Dirigeant */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-[#71717A] uppercase tracking-wider flex items-center gap-2">
                <User className="w-3.5 h-3.5" /> Dirigeant
              </h4>
              <div className="grid grid-cols-2 gap-2">
                <Input value={prenomDir} onChange={e => setPrenomDir(e.target.value)} placeholder="Prénom"
                  className="h-9 bg-white border-[#E5E7EB] text-sm" data-testid="dirigeant-prenom" />
                <Input value={nomDir} onChange={e => setNomDir(e.target.value)} placeholder="Nom"
                  className="h-9 bg-white border-[#E5E7EB] text-sm" data-testid="dirigeant-nom" />
              </div>
            </div>

            <Separator />

            {/* Prestataire en place */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-[#71717A] uppercase tracking-wider flex items-center gap-2">
                <Store className="w-3.5 h-3.5" /> Prestataire en place
              </h4>
              <Select value={prestataireActuel} onValueChange={setPrestataireActuel}>
                <SelectTrigger className="h-9 bg-white border-[#E5E7EB] text-sm" data-testid="prestataire-select">
                  <SelectValue placeholder="Aucun" />
                </SelectTrigger>
                <SelectContent>
                  {PRESTATAIRES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
              {prestataireActuel === 'Autre' && (
                <Input
                  value={prestataireAutre}
                  onChange={e => setPrestataireAutre(e.target.value)}
                  placeholder="Préciser le prestataire..."
                  className="h-9 bg-white border-[#E5E7EB] text-sm"
                  data-testid="prestataire-autre-input"
                />
              )}
            </div>

            <Separator />

            {/* Phone */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-[#71717A] uppercase tracking-wider flex items-center gap-2">
                <Phone className="w-3.5 h-3.5" /> Téléphone
              </h4>
              <div className="flex gap-2">
                <Input value={editPhone} onChange={e => setEditPhone(e.target.value)} placeholder="Numéro"
                  className="flex-1 h-9 font-mono" data-testid="edit-phone-input" />
                {prospect.url_source && (
                  <Button variant="ghost" size="sm" asChild className="h-9 w-9 p-0">
                    <a href={prospect.url_source} target="_blank" rel="noopener noreferrer"><ExternalLink className="w-3.5 h-3.5" /></a>
                  </Button>
                )}
              </div>
              {prospect.score_confiance > 0 && (
                <p className="text-xs text-[#71717A]">Score: <span className={`font-medium ${prospect.score_confiance >= 70 ? 'text-[#10B981]' : 'text-[#F59E0B]'}`}>{prospect.score_confiance}%</span> &bull; {prospect.source || '-'}</p>
              )}
            </div>

            <Separator />

            {/* Email professionnel */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-[#71717A] uppercase tracking-wider flex items-center gap-2">
                <Mail className="w-3.5 h-3.5" /> Email professionnel
              </h4>
              <Input
                type="email"
                value={emailPro}
                onChange={e => setEmailPro(e.target.value)}
                placeholder="prenom.nom@cooperative-u.fr"
                className="h-9 font-mono text-xs"
                data-testid="edit-email-pro-input"
              />
              {emailPro && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailPro.trim()) && (
                <p className="text-[11px] text-[#C2410C]">⚠️ Format d'email invalide</p>
              )}
              {emailPro && emailPro.trim() === (prospect.email_professionnel || '').trim() && prospect.email_professionnel && (
                <p className="text-[11px] text-[#71717A]">
                  ✓ Email actuel · sauvegarde automatique
                </p>
              )}
            </div>

            <Separator />

            {/* CA Alimentaire Estimatif */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-[#71717A] uppercase tracking-wider flex items-center gap-2">
                <Building2 className="w-3.5 h-3.5" /> CA Alimentaire Estimatif
              </h4>
              <div className="relative">
                <Input
                  type="text"
                  inputMode="numeric"
                  value={caAliment}
                  onChange={e => setCaAliment(e.target.value.replace(/[^\d]/g, ''))}
                  placeholder="12000000"
                  className="h-9 font-mono text-xs pr-16"
                  data-testid="edit-ca-aliment-input"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-[#71717A] pointer-events-none">€ / an</span>
              </div>
              {caAliment && parseInt(caAliment, 10) > 0 && (
                <p className="text-[11px] text-[#15803D] font-medium">
                  ≈ {parseInt(caAliment, 10) >= 1_000_000
                    ? `${(parseInt(caAliment, 10) / 1_000_000).toFixed(parseInt(caAliment, 10) % 1_000_000 === 0 ? 0 : 1).replace('.', ',')} M€`
                    : parseInt(caAliment, 10) >= 1_000
                      ? `${Math.round(parseInt(caAliment, 10) / 1_000)} K€`
                      : `${parseInt(caAliment, 10)} €`}
                </p>
              )}
            </div>

            <Separator />

            {/* Status Buttons */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-semibold text-[#71717A] uppercase tracking-wider">Résultat d'appel</h4>
                <AutoSavePill status={autoSaveStatus} />
              </div>
              <div className="grid grid-cols-2 gap-1.5">
                {COMMERCIAL_OPTIONS.map(opt => (
                  <Button key={opt.value} variant={statut === opt.value ? 'default' : 'outline'} size="sm"
                    onClick={() => addCallResult(opt.value)} data-testid={`call-result-${opt.value}`}
                    className={`text-xs h-7 ${statut === opt.value ? 'bg-[#0055FF] text-white' : ''}`}>
                    {opt.label}
                  </Button>
                ))}
              </div>
              {statut === 'rdv_obtenu' && (
                <div className="mt-2 p-3 rounded-lg bg-gradient-to-br from-[#FFF7ED] to-[#FEF3C7] border border-[#F97316]/30">
                  <div className="flex items-center gap-2 mb-2">
                    <BellRing className="w-4 h-4 text-[#F97316]" />
                    <p className="text-xs font-semibold text-[#9A3412]">Notification RDV</p>
                  </div>
                  {prospect.rdv_date_obtenu && (
                    <div className="bg-white rounded p-2 mb-2 text-xs flex items-center gap-2">
                      <CalendarDays className="w-3.5 h-3.5 text-[#10B981]" />
                      <span className="font-medium">RDV le {prospect.rdv_date_obtenu}</span>
                      {prospect.rdv_heure_obtenu && <span className="text-[#71717A]">à {prospect.rdv_heure_obtenu}</span>}
                    </div>
                  )}
                  <p className="text-xs text-[#71717A] mb-2">
                    Renvoyer aux destinataires configurés avec la fiche PDF en pièce jointe.
                  </p>
                  <Button onClick={openRdvDialog} size="sm" data-testid="send-rdv-email-btn"
                    className="w-full h-8 bg-[#F97316] hover:bg-[#EA580C] text-white gap-1.5 text-xs">
                    <Send className="w-3 h-3" /> Renvoyer email RDV
                  </Button>
                </div>
              )}
            </div>

            <Separator />

            {/* Commercial assigné */}
            <div className="space-y-2" data-testid="drawer-commercial-section">
              <h4 className="text-xs font-semibold text-[#71717A] uppercase tracking-wider flex items-center gap-2">
                <UserPlus className="w-3.5 h-3.5" /> Commercial en charge
              </h4>
              <Select
                value={prospect.commercial_assigne_id || '__none__'}
                onValueChange={assignCommercial}
                disabled={savingCommercial}
              >
                <SelectTrigger className="h-9 bg-white border-[#E5E7EB] text-sm" data-testid="drawer-commercial-select">
                  <SelectValue placeholder="Choisir un commercial..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__"><span className="text-[#A1A1AA] italic">Non assigné</span></SelectItem>
                  {commercials.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2 h-2 rounded-full" style={{ background: c.color || '#71717A' }}></span>
                        {c.prenom} {c.nom}{c.role ? ` · ${c.role}` : ''}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {prospect.commercial_assigne_nom && (
                <p className="text-[10px] text-[#71717A]">
                  Affecté automatiquement sur le département <strong>{(prospect.code_postal || '').slice(0,2)}</strong>. Modifiable à la volée.
                </p>
              )}
            </div>

            {/* Assignment */}
            {(user?.role === 'admin' || user?.role === 'manager') && commerciaux.length > 0 && (
              <div className="space-y-3">
                <h4 className="text-xs font-semibold text-[#71717A] uppercase tracking-wider flex items-center gap-2">
                  <UserPlus className="w-3.5 h-3.5" /> Assignation
                </h4>
                <Select value={assignTo} onValueChange={setAssignTo}>
                  <SelectTrigger className="h-9 bg-white" data-testid="assign-select"><SelectValue placeholder="Assigner à..." /></SelectTrigger>
                  <SelectContent>
                    {commerciaux.map(c => <SelectItem key={c.id} value={c.id}>{c.prenom} {c.nom}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Notes */}
            <div className="space-y-3">
              <h4 className="text-xs font-semibold text-[#71717A] uppercase tracking-wider flex items-center gap-2">
                <MessageSquare className="w-3.5 h-3.5" /> Note interne
              </h4>
              <Textarea value={comment} onChange={e => setComment(e.target.value)} placeholder="Note interne..."
                rows={2} className="bg-white border-[#E5E7EB] text-sm" data-testid="note-textarea" />
            </div>

            <Button onClick={saveChanges} disabled={loading} data-testid="save-prospect-btn"
              className="w-full h-10 bg-[#0055FF] hover:bg-[#0040CC] text-white gap-2 transition-all active:scale-[0.98]">
              <Save className="w-4 h-4" /> {loading ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </TabsContent>

          {/* TAB: Timeline / Suivi horodaté */}
          <TabsContent value="timeline" className="mt-0">
            {/* Add comment */}
            <div className="p-4 border-b border-[#E5E7EB] bg-white">
              <div className="flex gap-2">
                <Textarea value={newComment} onChange={e => setNewComment(e.target.value)}
                  placeholder="Ajouter un commentaire horodaté..." rows={2}
                  className="flex-1 bg-white border-[#E5E7EB] text-sm" data-testid="timeline-comment-input" />
                <Button onClick={postComment} disabled={!newComment.trim()} data-testid="post-comment-btn"
                  className="h-auto bg-[#F97316] hover:bg-[#EA580C] text-white self-end px-3">
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Timeline */}
            <div className="p-4" data-testid="prospect-timeline">
              {activites.length === 0 ? (
                <p className="text-sm text-[#71717A] text-center py-8">Aucune activité enregistrée. Ajoutez un commentaire ou effectuez une action.</p>
              ) : (
                <div className="relative">
                  {/* Vertical line */}
                  <div className="absolute left-[15px] top-2 bottom-2 w-px bg-[#E5E7EB]" />
                  <div className="space-y-4">
                    {activites.map(act => {
                      const IconComp = ACTIVITY_ICONS[act.type_activite] || MessageSquare;
                      const color = ACTIVITY_COLORS[act.type_activite] || '#71717A';
                      return (
                        <div key={act.id} className="flex gap-3 relative animate-fade-in" data-testid={`activity-${act.id}`}>
                          <div className="w-[30px] h-[30px] rounded-full flex items-center justify-center flex-shrink-0 z-10"
                            style={{ backgroundColor: `${color}15`, border: `2px solid ${color}30` }}>
                            <IconComp className="w-3.5 h-3.5" style={{ color }} />
                          </div>
                          <div className="flex-1 min-w-0 pb-1">
                            <div className="flex items-baseline justify-between gap-2">
                              <span className="text-xs font-semibold text-[#09090B]">{act.utilisateur_nom}</span>
                              <span className="text-[10px] text-[#71717A] whitespace-nowrap">{formatDate(act.date_creation)}</span>
                            </div>
                            <p className="text-sm text-[#09090B] mt-0.5 whitespace-pre-wrap">{act.contenu}</p>
                            {act.type_activite !== 'commentaire' && (
                              <Badge variant="secondary" className="text-[10px] mt-1 capitalize" style={{ backgroundColor: `${color}10`, color }}>
                                {act.type_activite}
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* TAB: Relances */}
          <TabsContent value="relances" className="mt-0">
            <div className="p-4 border-b border-[#E5E7EB] bg-white space-y-4">
              <h4 className="text-xs font-semibold text-[#71717A] uppercase tracking-wider">Programmer une relance</h4>

              {/* Calendar */}
              <div className="flex justify-center">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full h-10 justify-start text-left font-normal gap-2" data-testid="relance-calendar-trigger">
                      <CalendarDays className="w-4 h-4 text-[#F97316]" />
                      {relanceDate ? format(relanceDate, 'EEEE d MMMM yyyy', { locale: fr }) : 'Choisir une date...'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar mode="single" selected={relanceDate} onSelect={setRelanceDate}
                      locale={fr} disabled={(date) => date < new Date(new Date().setHours(0,0,0,0))}
                      className="rounded-md border" data-testid="relance-calendar" />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Type + Créneau */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-[#71717A] mb-1 block">Type</label>
                  <Select value={relanceType} onValueChange={setRelanceType}>
                    <SelectTrigger className="h-9 bg-white" data-testid="relance-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="telephone">Téléphone</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="visite">Visite</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs text-[#71717A] mb-1 block">Créneau</label>
                  <Select value={relanceCreneau} onValueChange={setRelanceCreneau}>
                    <SelectTrigger className="h-9 bg-white" data-testid="relance-creneau"><SelectValue placeholder="Créneau..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="matin">Matin (8h-12h)</SelectItem>
                      <SelectItem value="apres-midi">Après-midi (14h-18h)</SelectItem>
                      <SelectItem value="08:00">08:00</SelectItem>
                      <SelectItem value="08:30">08:30</SelectItem>
                      <SelectItem value="09:00">09:00</SelectItem>
                      <SelectItem value="09:30">09:30</SelectItem>
                      <SelectItem value="10:00">10:00</SelectItem>
                      <SelectItem value="10:30">10:30</SelectItem>
                      <SelectItem value="11:00">11:00</SelectItem>
                      <SelectItem value="11:30">11:30</SelectItem>
                      <SelectItem value="14:00">14:00</SelectItem>
                      <SelectItem value="14:30">14:30</SelectItem>
                      <SelectItem value="15:00">15:00</SelectItem>
                      <SelectItem value="15:30">15:30</SelectItem>
                      <SelectItem value="16:00">16:00</SelectItem>
                      <SelectItem value="16:30">16:30</SelectItem>
                      <SelectItem value="17:00">17:00</SelectItem>
                      <SelectItem value="17:30">17:30</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Commentaire */}
              <Textarea value={relanceCommentaire} onChange={e => setRelanceCommentaire(e.target.value)}
                placeholder="Note pour cette relance (optionnel)..." rows={2}
                className="bg-white border-[#E5E7EB] text-sm" data-testid="relance-commentaire" />

              <Button onClick={createRelance} data-testid="create-relance-btn"
                className="w-full h-10 bg-[#F97316] hover:bg-[#EA580C] text-white gap-2">
                <CalendarPlus className="w-4 h-4" /> Planifier la relance
              </Button>
            </div>

            <div className="p-4 space-y-3" data-testid="relances-list">
              {relances.length === 0 ? (
                <p className="text-sm text-[#71717A] text-center py-6">Aucune relance programmée</p>
              ) : (
                relances.map(r => {
                  const isPast = new Date(r.date_relance) < new Date() && r.statut === 'planifiee';
                  const TypeIcon = r.type_relance === 'email' ? Mail : r.type_relance === 'visite' ? MapPinned : PhoneCall;
                  return (
                    <div key={r.id} className={`p-3 rounded-lg border ${r.statut === 'effectuee' ? 'bg-[#ECFDF5] border-[#10B981]/20' : isPast ? 'bg-[#FEF2F2] border-[#EF4444]/20' : 'bg-[#FFFBEB] border-[#F59E0B]/20'}`}
                      data-testid={`relance-${r.id}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 flex-wrap">
                          <TypeIcon className={`w-4 h-4 ${r.statut === 'effectuee' ? 'text-[#10B981]' : isPast ? 'text-[#EF4444]' : 'text-[#F59E0B]'}`} />
                          <span className="text-sm font-medium capitalize">{r.type_relance}</span>
                          <span className="text-xs text-[#71717A]">{new Date(r.date_relance).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })}</span>
                          {r.creneau && <Badge variant="secondary" className="text-[10px]">{r.creneau}</Badge>}
                          {isPast && <Badge variant="secondary" className="text-[10px] bg-[#FEF2F2] text-[#EF4444]">En retard</Badge>}
                        </div>
                        {r.statut === 'planifiee' && (
                          <Button size="sm" variant="outline" onClick={() => markRelanceDone(r.id)}
                            className="h-7 text-xs gap-1 text-[#10B981] border-[#10B981] hover:bg-[#10B981]/5" data-testid={`complete-relance-${r.id}`}>
                            <CheckCircle className="w-3 h-3" /> Fait
                          </Button>
                        )}
                        {r.statut === 'effectuee' && (
                          <Badge variant="secondary" className="text-xs bg-[#ECFDF5] text-[#10B981]">Effectuée</Badge>
                        )}
                      </div>
                      {r.commentaire && <p className="text-xs text-[#71717A] mt-1.5">{r.commentaire}</p>}
                    </div>
                  );
                })
              )}
            </div>
          </TabsContent>
        </Tabs>
      </SheetContent>
    </Sheet>
    <Dialog open={rdvDateDialogOpen} onOpenChange={setRdvDateDialogOpen}>
      <DialogContent className="max-w-md" data-testid="rdv-date-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-[#10B981]" />
            Date du RDV obtenu
          </DialogTitle>
          <DialogDescription className="text-xs">
            Renseigne la date et l'heure du rendez-vous avec {prospect.prenom_dirigeant} {prospect.nom_dirigeant || prospect.nom_magasin}.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-sm mb-1.5 block">Date</Label>
              <Input
                type="date"
                value={rdvDate}
                onChange={e => setRdvDate(e.target.value)}
                className="h-10 bg-white"
                data-testid="rdv-date-input"
              />
            </div>
            <div>
              <Label className="text-sm mb-1.5 block">Heure</Label>
              <Input
                type="time"
                value={rdvHeure}
                onChange={e => setRdvHeure(e.target.value)}
                className="h-10 bg-white"
                data-testid="rdv-heure-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-sm mb-1.5 block">Note pour le commercial (optionnel)</Label>
            <Textarea
              value={rdvNote}
              onChange={e => setRdvNote(e.target.value)}
              placeholder="Contexte, point d'attention, demande du prospect..."
              rows={3}
              className="bg-white text-sm"
              data-testid="rdv-note-input"
            />
          </div>
          <div className="p-2.5 bg-[#FFF7ED] rounded-lg text-xs text-[#9A3412] flex items-start gap-2">
            <BellRing className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
            <span>Après enregistrement, vous choisirez les destinataires à notifier (avec la fiche PDF en pièce jointe).</span>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setRdvDateDialogOpen(false)} size="sm">Annuler</Button>
          <Button
            onClick={saveRdvAndPickRecipients}
            disabled={savingRdv || !rdvDate}
            size="sm"
            className="bg-[#10B981] hover:bg-[#059669] gap-1.5"
            data-testid="rdv-save-and-send-btn"
          >
            <Send className="w-3.5 h-3.5" />
            {savingRdv ? 'Enregistrement...' : 'Enregistrer et envoyer'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    <Dialog open={rdvDialogOpen} onOpenChange={setRdvDialogOpen}>
      <DialogContent className="max-w-md" data-testid="rdv-recipients-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="w-4 h-4 text-[#F97316]" />
            Notifier le RDV
          </DialogTitle>
          <DialogDescription className="text-xs">
            Sélectionnez les personnes à notifier par email. La fiche PDF sera jointe.
          </DialogDescription>
        </DialogHeader>
        <div className="py-2 space-y-2 max-h-80 overflow-y-auto">
          {availableRecipients.length === 0 ? (
            <div className="p-4 bg-[#FEF2F2] border border-[#FCA5A5] rounded-lg">
              <p className="text-sm font-medium text-[#B91C1C]">Aucun destinataire configuré</p>
              <p className="text-xs text-[#991B1B] mt-1">Va dans Paramètres → Notifications RDV pour ajouter des destinataires.</p>
            </div>
          ) : (
            availableRecipients.map((r, idx) => (
              <label key={idx} className="flex items-center gap-3 p-2.5 rounded-lg border border-[#E5E7EB] cursor-pointer hover:bg-[#F9FAFB] transition-colors" data-testid={`rdv-recipient-choice-${idx}`}>
                <Checkbox
                  checked={selectedEmails.includes(r.email)}
                  onCheckedChange={() => toggleRecipient(r.email)}
                  data-testid={`rdv-recipient-check-${idx}`}
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium truncate">{r.label}</p>
                  <p className="text-xs text-[#71717A] font-mono truncate">{r.email}</p>
                </div>
              </label>
            ))
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setRdvDialogOpen(false)} size="sm">Annuler</Button>
          <Button
            onClick={confirmSendRdv}
            disabled={sendingRdv || selectedEmails.length === 0 || availableRecipients.length === 0}
            size="sm"
            className="bg-[#F97316] hover:bg-[#EA580C] gap-1.5"
            data-testid="rdv-confirm-send-btn"
          >
            <Send className="w-3.5 h-3.5" />
            {sendingRdv ? 'Envoi...' : `Envoyer à ${selectedEmails.length} destinataire${selectedEmails.length > 1 ? 's' : ''}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
}
